// Settings — 5 cards: Scan schedule · Notifications · LLM · CV · Portal sources.
// Job search preferences and display filters now live on the Scanner page.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import LogoSquare from '../components/ui/LogoSquare';
import Chip from '../components/ui/Chip';
import Drawer from '../components/ui/Drawer';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import type { Portal, UserProfile } from '../lib/types';

type Provider = 'ollama' | 'anthropic' | 'openai' | 'groq' | 'deepseek';

interface CuratedModel {
  id: string;
  label: string;
}

type EngineBackend = 'ollama' | 'claude';
interface LlmConfig {
  provider: Provider;
  model: string;
  has_api_key: boolean;
  api_key_mask: string;
  curated_models: Partial<Record<Provider, CuratedModel[]>>;
  eval_backend?: EngineBackend;
  backends?: Record<string, EngineBackend>;
}

// The 7 curated functions that get their own backend dropdown. Keys match the
// server's FUNCTION_BACKEND_KEYS; values persist to config/llm.yml as
// backend_<key> and are honored at job-spawn time.
const FUNCTION_BACKENDS: { key: string; label: string }[] = [
  { key: 'evaluate',     label: '职位评估 · Job evaluation' },
  { key: 'cv_writer',    label: 'CV Writer · 简历定制' },
  { key: 'cover_letter', label: '求职信 · Cover letter' },
  { key: 'interview',    label: '面试准备 · Interview prep' },
  { key: 'research',     label: '公司调研 · Deep research' },
  { key: 'compare',      label: 'Offer 对比 · Compare offers' },
  { key: 'salary',       label: '薪资谈判 · Salary negotiation' },
];

const PROVIDER_INFO: Record<Provider, { label: string; cost: string; help: string; signupUrl?: string; needsKey: boolean }> = {
  ollama:    { label: 'Shared Ollama (host)',                   cost: 'Free — uses the maintainer\'s machine',          needsKey: false, help: 'No API key needed. Routes through whatever model the host has installed locally. Speed depends on how busy other tenants are. Best for privacy: prompts never leave the host.' },
  anthropic: { label: 'Anthropic Claude (BYOK)',                cost: '~$0.80–15/M tokens depending on model',           needsKey: true,  help: 'Best overall quality, especially for nuanced CV-vs-JD reasoning. Pick Haiku 4.5 for routine evals, Sonnet 4.6 for tough negotiations.', signupUrl: 'https://console.anthropic.com/settings/keys' },
  openai:    { label: 'OpenAI (BYOK)',                          cost: '~$0.15–30/M tokens depending on model',           needsKey: true,  help: 'gpt-4o-mini is the workhorse. The o1/o3 reasoning models are slower but better at multi-step problems like negotiation strategy.', signupUrl: 'https://platform.openai.com/api-keys' },
  groq:      { label: 'Groq (BYOK, generous free tier)',        cost: 'Free 30 req/min, then ~$0.05–0.79/M tokens',      needsKey: true,  help: 'Fastest open-weights hosting (Llama 3.3 70B at 250+ tok/s). Sign up takes 30 seconds and the free tier covers most casual use.', signupUrl: 'https://console.groq.com/keys' },
  deepseek:  { label: 'DeepSeek (BYOK, very cheap)',            cost: '~$0.14–0.28/M tokens',                            needsKey: true,  help: 'Chinese state-of-the-art models at 5–10× cheaper than US labs. Reasoner is their R1-style model with deep thinking.', signupUrl: 'https://platform.deepseek.com/api_keys' },
};

interface CVStatus {
  file: string | null;
  chars: number;
  preview: string;
}

// Re-runs the cv_extract_profile job and resolves when it finishes (or
// times out). Identical helper to the one in Profile.tsx / UploadCV.tsx —
// duplicated rather than centralised to keep the route components free
// of cross-route imports.
async function reparseProfile(timeoutMs = 90_000): Promise<{ ok: boolean; reason?: string }> {
  const submit = await fetch('/api/jobs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cmd: 'cv_extract_profile', payload: '' }),
  });
  if (!submit.ok) return { ok: false, reason: `submit ${submit.status}` };
  const { job_id } = await submit.json();
  const t0 = Date.now();
  while (Date.now() - t0 < timeoutMs) {
    await new Promise((r) => setTimeout(r, 1500));
    const all = await fetch('/api/jobs').then((r) => r.json());
    const me = (all.jobs || []).find((j: { id: string }) => j.id === job_id);
    if (!me) return { ok: false, reason: 'job vanished' };
    if (me.status === 'done') return { ok: true };
    if (me.status === 'failed') return { ok: false, reason: 'job failed' };
  }
  return { ok: false, reason: 'timeout' };
}

// ────────────────────────────────────────────────────────────────────────────
// LLM provider card — choose Ollama (shared) or BYOK (Anthropic/OpenAI/Groq)
// ────────────────────────────────────────────────────────────────────────────

function LlmCard() {
  const qc = useQueryClient();
  const cfgQ = useQuery({
    queryKey: ['llm-config'],
    queryFn: () => apiGet<LlmConfig>('/api/llm-config'),
  });
  const [provider, setProvider] = useState<Provider>('ollama');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [backends, setBackends] = useState<Record<string, EngineBackend>>(
    Object.fromEntries(FUNCTION_BACKENDS.map((f) => [f.key, 'ollama'])),
  );
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (cfgQ.data) {
      setProvider(cfgQ.data.provider);
      setModel(cfgQ.data.model || '');
      const srv = cfgQ.data.backends || {};
      setBackends(Object.fromEntries(
        FUNCTION_BACKENDS.map((f) => [f.key, srv[f.key] === 'claude' ? 'claude' : 'ollama']),
      ));
    }
  }, [cfgQ.data]);

  const info = PROVIDER_INFO[provider];
  const needsKey = info.needsKey;
  const curated = cfgQ.data?.curated_models?.[provider] || [];
  const visibleProviders: Provider[] = ['ollama', 'anthropic', 'openai', 'groq', 'deepseek'];

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const backendFields = Object.fromEntries(
        FUNCTION_BACKENDS.map((f) => [`backend_${f.key}`, backends[f.key] || 'ollama']),
      );
      const r = await apiPost<{ ok: boolean }>('/api/llm-config', {
        provider, model: model.trim(), api_key: apiKey.trim(),
        ...backendFields,
      });
      if (r.ok) {
        setMsg('✓ Saved.');
        setApiKey('');
        qc.invalidateQueries({ queryKey: ['llm-config'] });
      } else {
        setMsg('Save failed.');
      }
    } catch (e) {
      setMsg(`Save failed: ${(e as Error).message}`);
    }
    setSaving(false);
    setTimeout(() => setMsg(null), 5000);
  }

  async function test() {
    setTesting(true);
    setMsg(`Testing ${provider}…`);
    try {
      // Use the just-typed key if present; otherwise the server falls
      // back to the saved one. PATCH-with-test would be neater but the
      // user expects "test before save" behaviour here.
      const body: Record<string, string> = { provider, model: model.trim() };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const r = await fetch('/api/llm-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then((x) => x.json());
      setMsg(r.ok ? `✓ Connection OK (${r.ms} ms).` : `✗ Test failed. Check the model name and API key.`);
    } catch (e) {
      setMsg(`Test failed: ${(e as Error).message}`);
    }
    setTesting(false);
    setTimeout(() => setMsg(null), 8000);
  }

  async function clearKey() {
    if (!confirm('Forget your saved API key and revert to the shared Ollama? You can always add it back later.')) return;
    await fetch('/api/llm-config', { method: 'DELETE' });
    setProvider('ollama');
    setModel('');
    setApiKey('');
    setMsg('✓ Cleared. Now using shared Ollama.');
    qc.invalidateQueries({ queryKey: ['llm-config'] });
    setTimeout(() => setMsg(null), 5000);
  }

  return (
    <div className="card" style={{ padding: 26 }}>
      <div className="serif" style={{ fontSize: 20, marginBottom: 6 }}>LLM provider</div>
      <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 16 }}>
        Pick whose model runs your evaluations. Default is the host's local Ollama (free,
        slower under contention). Or plug in your own Anthropic / OpenAI / Groq API key
        — the key stays in <code>users/&lt;you&gt;/config/llm.yml</code> on the host machine,
        masked everywhere on read, never logged.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
        {visibleProviders.map((p) => {
          const i = PROVIDER_INFO[p];
          const selected = provider === p;
          return (
            <button
              key={p}
              type="button"
              onClick={() => { setProvider(p); setModel(''); }}
              style={{
                textAlign: 'left',
                padding: '12px 14px',
                borderRadius: 8,
                border: `1.5px solid ${selected ? 'var(--pri)' : 'var(--line)'}`,
                background: selected ? 'var(--pri-soft)' : 'var(--surface)',
                cursor: 'pointer',
                transition: 'border-color 120ms',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{i.label}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 4 }}>{i.cost}</div>
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: 12, color: 'var(--ink2)', marginBottom: 14, lineHeight: 1.55 }}>
        {info.help}
        {info.signupUrl && (
          <>
            {' '}
            <a href={info.signupUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--pri)' }}>
              Get key →
            </a>
          </>
        )}
      </div>

      {needsKey && (
        <>
          <Row label="API key">
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfgQ.data?.has_api_key ? `current: ${cfgQ.data.api_key_mask} (leave empty to keep)` : 'sk-…'}
              style={inputStyle}
            />
          </Row>
          {curated.length > 0 ? (
            <Row label="Model">
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                style={inputStyle}
              >
                {/* Empty value = "use the dispatcher's default for this provider" */}
                <option value="">Default ({curated[0]?.id})</option>
                {curated.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </Row>
          ) : (
            <Row label="Model">
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="leave empty for default"
                style={inputStyle}
              />
            </Row>
          )}
        </>
      )}

      {/* Per-function engine — which LLM runs each function. Independent of the
          API provider above: 'Claude' uses the laptop's Max subscription
          (`claude -p`; VM functions route via the broker) and auto-falls back
          to Ollama if Claude is unavailable (quota hit / laptop offline). */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', marginBottom: 4 }}>各功能引擎 · Per-function engine</div>
        <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginBottom: 12, lineHeight: 1.5 }}>
          为每个功能单独选择后端。<b>Claude</b> 用你笔记本的 Max 订阅 (<code>claude -p</code>)，质量更高；
          不可用时(配额用尽 / 笔记本离线)自动回退到本地 <b>Ollama</b>。其余内部任务默认用 Ollama。
        </div>
        {FUNCTION_BACKENDS.map((f) => (
          <Row key={f.key} label={f.label}>
            <select
              value={backends[f.key] || 'ollama'}
              onChange={(e) => setBackends((b) => ({ ...b, [f.key]: e.target.value as EngineBackend }))}
              style={inputStyle}
            >
              <option value="ollama">Ollama (本地 · 免费)</option>
              <option value="claude">Claude (Max 订阅)</option>
            </select>
          </Row>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <button className="btn primary" disabled={saving || testing} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn" disabled={saving || testing} onClick={test}>
          {testing ? 'Testing…' : 'Test connection'}
        </button>
        {cfgQ.data?.has_api_key && (
          <button className="btn ghost" disabled={saving || testing} onClick={clearKey}>
            Clear key &amp; revert to Ollama
          </button>
        )}
      </div>

      {msg && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid var(--line)',
            background: msg.startsWith('✓') ? 'var(--pri-soft)' : 'var(--surface)',
            color: msg.startsWith('✓') ? 'var(--pri)' : 'var(--ink2)',
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          {msg}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CV card — replace the master CV + reparse the contact block
// ────────────────────────────────────────────────────────────────────────────

function CVCard() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const statusQ = useQuery({
    queryKey: ['cv-status'],
    queryFn: () => apiGet<CVStatus>('/api/cv-status'),
  });

  const handleFile = async (file: File) => {
    setMsg(null);
    setUploading(true);
    try {
      const buf = await file.arrayBuffer();
      const res = await fetch('/api/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream', 'X-Filename': encodeURIComponent(file.name) },
        body: buf,
      });
      if (!res.ok) throw new Error(`upload ${res.status}`);
      statusQ.refetch();
    } catch (e) {
      setMsg(`Upload failed: ${(e as Error).message}`);
      setUploading(false);
      return;
    }
    setUploading(false);
    setParsing(true);
    setMsg('Asking Ollama to refresh your profile from the new CV…');
    const r = await reparseProfile();
    setParsing(false);
    if (r.ok) {
      setMsg('✓ Profile refreshed. Existing fields you typed were left untouched.');
      qc.invalidateQueries({ queryKey: ['profile'] });
    } else {
      setMsg(`Couldn't refresh profile: ${r.reason}. CV file is saved; retry from Profile.`);
    }
  };

  return (
    <div className="card" style={{ padding: 26 }}>
      <div className="serif" style={{ fontSize: 20, marginBottom: 6 }}>CV</div>
      <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 16 }}>
        Replace your master CV. Each upload runs the local Ollama parser to refresh
        your name, email, location, etc. — fields you've already typed are kept.
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 14 }}>
        Current:&nbsp;
        {statusQ.data?.file
          ? <><strong>{statusQ.data.file}</strong> · {statusQ.data.chars} chars</>
          : <em style={{ color: 'var(--ink3)' }}>none yet — upload below</em>}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.md,.txt"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        <button
          className="btn primary"
          disabled={uploading || parsing}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? 'Uploading…' : parsing ? 'Parsing…' : 'Upload new CV'}
        </button>
        <button
          className="btn"
          disabled={uploading || parsing}
          onClick={async () => {
            setParsing(true);
            setMsg('Re-parsing existing CV with Ollama…');
            const r = await reparseProfile();
            setParsing(false);
            if (r.ok) {
              setMsg('✓ Profile refreshed.');
              qc.invalidateQueries({ queryKey: ['profile'] });
            } else {
              setMsg(`Re-parse failed: ${r.reason}.`);
            }
          }}
          title="Run the parser again on the CV that's already on file."
        >
          Re-parse current CV
        </button>
        <button className="btn ghost" onClick={() => nav('/cv/upload')}>
          Open full uploader →
        </button>
      </div>
      {msg && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid var(--line)',
            background: msg.startsWith('✓') ? 'var(--pri-soft)' : 'var(--surface)',
            color: msg.startsWith('✓') ? 'var(--pri)' : 'var(--ink2)',
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          {parsing ? 'Parsing… (Ollama may take 30–90 s on first use)' : msg}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['profile'],
    queryFn: () => apiGet<UserProfile>('/api/profile'),
  });
  const [form, setForm] = useState<Partial<UserProfile>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (q.data) {
      setForm({
        scan_cadence_hours: q.data.scan_cadence_hours ?? 6,
        quiet_hours: q.data.quiet_hours ?? ['22:00', '07:00'],
        digest_time: q.data.digest_time ?? '09:00',
        notification_prefs: q.data.notification_prefs ?? { email_daily_digest: true, browser: false },
      });
    }
  }, [q.data]);

  const mut = useMutation({
    mutationFn: (body: Partial<UserProfile>) => apiPost('/api/profile', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    },
  });

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="Workspace"
        title="Settings."
        sub="Schedule, notifications, and the list of portals we scan."
        actions={
          <button className="btn primary" disabled={mut.isPending} onClick={() => mut.mutate(form)}>
            {mut.isPending ? 'Saving…' : saved ? '✓ Saved' : 'Save settings'}
          </button>
        }
      />

      {/* Row 1: Scan schedule + Notifications */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="card" style={{ padding: 26 }}>
          <div className="serif" style={{ fontSize: 20, marginBottom: 16 }}>Scan schedule</div>
          <Row label="Run every (hours)">
            <input
              type="number" min={1} max={72}
              value={form.scan_cadence_hours ?? 6}
              onChange={(e) => setForm({ ...form, scan_cadence_hours: +e.target.value })}
              style={inputStyle}
            />
          </Row>
          <Row label="Quiet hours (start)">
            <input
              type="time"
              value={form.quiet_hours?.[0] ?? '22:00'}
              onChange={(e) => setForm({ ...form, quiet_hours: [e.target.value, form.quiet_hours?.[1] ?? '07:00'] })}
              style={inputStyle}
            />
          </Row>
          <Row label="Quiet hours (end)">
            <input
              type="time"
              value={form.quiet_hours?.[1] ?? '07:00'}
              onChange={(e) => setForm({ ...form, quiet_hours: [form.quiet_hours?.[0] ?? '22:00', e.target.value] })}
              style={inputStyle}
            />
          </Row>
          <Row label="Daily digest at">
            <input
              type="time"
              value={form.digest_time ?? '09:00'}
              onChange={(e) => setForm({ ...form, digest_time: e.target.value })}
              style={inputStyle}
            />
          </Row>
        </div>

        <div className="card" style={{ padding: 26 }}>
          <div className="serif" style={{ fontSize: 20, marginBottom: 16 }}>Notifications</div>
          <Toggle
            label="Email daily digest"
            on={!!form.notification_prefs?.email_daily_digest}
            onToggle={() =>
              setForm({
                ...form,
                notification_prefs: {
                  ...(form.notification_prefs ?? {}),
                  email_daily_digest: !form.notification_prefs?.email_daily_digest,
                },
              })
            }
          />
          <Toggle
            label="Browser notifications for new high-fit matches"
            on={!!form.notification_prefs?.browser}
            onToggle={() =>
              setForm({
                ...form,
                notification_prefs: {
                  ...(form.notification_prefs ?? {}),
                  browser: !form.notification_prefs?.browser,
                },
              })
            }
          />
        </div>
      </div>

      {/* Row 2: LLM provider (full width) */}
      <div style={{ marginBottom: 20 }}>
        <LlmCard />
      </div>

      {/* Row 3: CV (full width) */}
      <div style={{ marginBottom: 20 }}>
        <CVCard />
      </div>

      {/* Row 4: Portal sources (full width) */}
      <PortalSourcesCard />

      {/* Row 5: Liveness — daily background re-check of every report's URL. */}
      <LivenessCard />

      {/* Row 6: Danger zone — destructive account-data wipe. */}
      <DangerZoneCard />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Liveness — daily sweep status + manual trigger
// ────────────────────────────────────────────────────────────────────────────
//
// The server's livenessSweepAllTenants() ticks every 24h and writes
// data/liveness-cache.json per tenant. This card surfaces:
//   - When the last sweep ran (ISO timestamp from GET /api/liveness/status)
//   - Breakdown of live / expired / unknown across all reports
//   - "Run now" button that POSTs /api/liveness/sweep
//
// The chip on each MatchRow / ApplicationRow is the consumer side of the
// same cache — fixing the chip is the whole point of this card.

function LivenessCard() {
  const [status, setStatus] = useState<{
    lastSweepAt: string | null;
    total: number;
    counts: { live: number; expired: number; unknown: number };
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const r = await fetch('/api/liveness/status');
      if (r.ok) setStatus(await r.json());
    } catch { /* noop */ }
  };

  useEffect(() => { refresh(); }, []);

  const runSweep = async () => {
    if (busy) return;
    setBusy(true);
    setMsg('Sweep started — Playwright is checking each URL in the background. This page will refresh in 30s.');
    try {
      const r = await fetch('/api/liveness/sweep', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: false, limit: 1000 }),
      });
      const j = await r.json();
      if (j.status === 'done') {
        setMsg(`Sweep done: ${j.checked} checked, ${j.skipped} skipped (fresh).`);
        refresh();
      } else {
        setMsg(`Sweep started — checking up to ${j.limit} URLs in the background. ~5s per URL via Playwright. Status panel will refresh every 30s; chips on /matches show up as each row finishes.`);
        // Poll status every 30s so the user sees the live/expired counts climb
        const poll = setInterval(refresh, 30_000);
        setTimeout(() => clearInterval(poll), 30 * 60_000);
      }
    } catch (e) {
      setMsg(`Failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const lastSweepLabel = status?.lastSweepAt ? formatTimeAgo(status.lastSweepAt) : 'never';
  return (
    <div className="card" style={{ marginTop: 20 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600 }}>
        Posting liveness
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--ink2)' }}>
        Every 24h the system re-checks each report's job URL via Playwright. Results show as Live / Expired chips on /matches and /apps. Expired postings are sticky — once marked expired, they aren't re-checked.
      </p>
      {status && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12.5, color: 'var(--ink2)', marginBottom: 10, flexWrap: 'wrap' }}>
          <span>Last sweep: <b>{lastSweepLabel}</b></span>
          <span>·</span>
          <span>{status.total} reports cached</span>
          <span>·</span>
          <span style={{ color: '#1f6f3a' }}>{status.counts.live ?? 0} live</span>
          <span style={{ color: '#a13030' }}>{status.counts.expired ?? 0} expired</span>
          <span>{status.counts.unknown ?? 0} unknown</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={runSweep} disabled={busy}>
          {busy ? 'Running…' : 'Run sweep now'}
        </button>
        <button className="btn ghost" onClick={refresh}>Refresh status</button>
      </div>
      {msg && <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 8 }}>{msg}</div>}
    </div>
  );
}

function formatTimeAgo(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'unknown';
  const ms = Date.now() - t;
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ────────────────────────────────────────────────────────────────────────────
// Danger zone — wipe all account data
// ────────────────────────────────────────────────────────────────────────────
//
// Hits DELETE /api/account/data with the same confirm-query +
// confirm-header guard /api/matches uses. Two-click armed pattern at
// the button level matches the rest of the app's destructive UX.
// User can opt out of the CV file + title-filter being wiped via
// checkboxes; both default ON (= keep) so the most common "I want to
// re-scan from scratch" flow doesn't force re-uploading the CV.

function DangerZoneCard() {
  const qc = useQueryClient();
  const [keepCV, setKeepCV] = useState(true);
  const [keepTitleFilter, setKeepTitleFilter] = useState(true);
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // 3-second auto-revert on the armed state — same UX as Matches Clear all.
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);

  const onClick = async () => {
    if (busy) return;
    if (!armed) { setArmed(true); return; }
    setArmed(false);
    setBusy(true);
    setMsg('Wiping…');
    try {
      const r = await fetch('/api/account/data?confirm=true', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Confirm-Destructive': 'yes',
        },
        body: JSON.stringify({ keepCV, keepTitleFilter }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      setMsg(
        `✓ Wiped: ${j.cancelledJobs} job${j.cancelledJobs === 1 ? '' : 's'} cancelled, ` +
        `${j.filesRemoved} file${j.filesRemoved === 1 ? '' : 's'} removed, ` +
        `${j.dbRowsRemoved} DB row${j.dbRowsRemoved === 1 ? '' : 's'} removed. ` +
        (keepCV ? 'CV kept. ' : 'CV removed. ') +
        'Reloading…'
      );
      // Wipe SPA-side localStorage that holds queues / orchestration
      // pointing at URLs that no longer exist server-side. Without this,
      // useJobRunnerGroup's persisted "Evaluate all" queue would silently
      // re-fire /api/jobs for the same URLs once they get re-scraped — the
      // motivating bug ("scan 完了 直接走到 evaluated"). Listed explicitly
      // (not a blanket localStorage.clear()) so we don't blow away unrelated
      // settings like onboarding step or auth scaffolding.
      try {
        const KEYS = [
          'nice2job.matches.evaluations',  // Evaluate-all queue
          'nice2job.scanner.orchestration', // legacy two-stage scan orch (Scanner)
          'nice2job.scanner.showDropped',  // Scanner audit toggle
          'nice2job.cv.selected',          // CVPicker selected-set
        ];
        for (const k of KEYS) localStorage.removeItem(k);
        // Remove any per-job runner state too (keys like nice2job.job.<id>).
        const toRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith('nice2job.job.')) toRemove.push(k);
        }
        for (const k of toRemove) localStorage.removeItem(k);
      } catch { /* private mode etc */ }
      // Hard reload so every component remounts with fresh state — react-
      // query invalidation alone isn't enough because hooks like
      // useJobRunnerGroup keep their useState entries even after their
      // queryClient gets bumped.
      qc.invalidateQueries({ queryKey: ['matches'] });
      qc.invalidateQueries({ queryKey: ['matches-pending'] });
      qc.invalidateQueries({ queryKey: ['matches-pending-count'] });
      qc.invalidateQueries({ queryKey: ['scan-history'] });
      qc.invalidateQueries({ queryKey: ['auto-scan'] });
      qc.invalidateQueries({ queryKey: ['cv-tailored'] });
      qc.invalidateQueries({ queryKey: ['cv-status'] });
      qc.invalidateQueries({ queryKey: ['title-filter'] });
      qc.invalidateQueries({ queryKey: ['profile'] });
      // 1.5 s delay so the user sees the success toast before we navigate.
      setTimeout(() => { window.location.reload(); }, 1500);
    } catch (e) {
      setMsg(`Wipe failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="card"
      style={{
        padding: 26,
        marginTop: 22,
        border: '1px solid #b43a3a',
        background: 'rgba(180,58,58,0.04)',
      }}
    >
      <div className="serif" style={{ fontSize: 20, marginBottom: 6, color: '#b43a3a' }}>
        Danger zone
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink2)', marginBottom: 16, lineHeight: 1.55 }}>
        Wipe everything generated from your scans and evaluations — reports,
        scan history, JD cache, applications tracker, tailored CV PDFs,
        per-pair scan state. Useful when you want to start fresh with the
        latest pipeline. <strong>Cannot be undone.</strong>
        <br />
        <br />
        <span style={{ color: 'var(--ink3)' }}>
          Always preserved: your account, profile, LLM provider config, the
          portals you've added, your job preferences. Optionally preserved
          (defaults below): your uploaded CV file and the CV-derived title
          filter.
        </span>
      </div>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={keepCV}
            onChange={(e) => setKeepCV(e.target.checked)}
            disabled={busy}
          />
          Keep my uploaded CV file
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={keepTitleFilter}
            onChange={(e) => setKeepTitleFilter(e.target.checked)}
            disabled={busy}
          />
          Keep CV-derived title filter (anchors / lead discipline / seniority)
        </label>
      </div>

      <button
        className="btn"
        onClick={onClick}
        disabled={busy}
        style={{
          color: armed ? '#fff' : '#b43a3a',
          background: armed ? '#b43a3a' : 'transparent',
          border: '1px solid #b43a3a',
          fontSize: 12.5,
          padding: '8px 14px',
          transition: 'all 120ms',
        }}
        title={armed ? 'Click again to permanently wipe everything' : 'Wipe all generated data for this account'}
      >
        {busy ? 'Wiping…' : armed ? 'Click again to confirm' : 'Wipe all data'}
      </button>

      {msg && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid var(--line)',
            background: msg.startsWith('✓') ? 'var(--pri-soft)' : 'var(--surface)',
            color: msg.startsWith('✓') ? 'var(--pri)' : 'var(--ink2)',
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          {msg}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Portal sources card
// ────────────────────────────────────────────────────────────────────────────

function PortalSourcesCard() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const portalsQ = useQuery({
    queryKey: ['portals'],
    queryFn: () => apiGet<Portal[]>('/api/portals'),
  });
  const portals = portalsQ.data ?? [];

  const deleteMut = useMutation({
    mutationFn: (name: string) => apiDelete(`/api/portals/${encodeURIComponent(name)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portals'] }),
  });

  return (
    <>
      <AddPortalDrawer open={addOpen} onClose={() => setAddOpen(false)} />

      {/* Title filter sub-section — link to the chip editor. Inlined as
          a one-card link so the main Settings page stays focused on
          portals; the editor itself lives at /app/settings/filter. */}
      <div
        className="card"
        style={{ padding: 22, marginBottom: 22, cursor: 'pointer' }}
        onClick={() => nav('/settings/filter')}
        role="link"
        tabIndex={0}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div className="serif" style={{ fontSize: 18 }}>Title filter</div>
            <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 4, lineHeight: 1.55 }}>
              CV-derived auto filter — drops obviously irrelevant titles before they reach evaluation. Edit anchor keywords or disable here.
            </div>
          </div>
          <span style={{ color: 'var(--ink3)', fontSize: 18 }}>→</span>
        </div>
      </div>

      <div className="card" style={{ padding: 26 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 16 }}>
          <div>
            <div className="serif" style={{ fontSize: 20 }}>Portal sources</div>
            <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 2 }}>
              Company ATS endpoints we hit directly. External sources (LinkedIn, Indeed…) don't need configuration here.
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <button className="btn primary" onClick={() => setAddOpen(true)}>+ Add portal</button>
        </div>

        <div style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
          <div
            className="row-head"
            style={{ gridTemplateColumns: '1.2fr 0.8fr 2fr 0.8fr 0.8fr 0.3fr', position: 'sticky', top: 0, zIndex: 1 }}
          >
            {['Portal', 'Kind', 'Careers URL', 'Last scan', 'Status', ''].map((h) => (
              <div key={h} className="tab">{h}</div>
            ))}
          </div>
          {portalsQ.isLoading && <div className="empty">Loading portals…</div>}
          {portalsQ.isError && <div className="empty">Couldn't load portals.yml.</div>}
          {portals.map((p) => (
            <div
              key={p.name}
              className="row"
              style={{ gridTemplateColumns: '1.2fr 0.8fr 2fr 0.8fr 0.8fr 0.3fr', cursor: 'default' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <LogoSquare name={p.name} size={26} />
                <span style={{ fontWeight: 500 }}>{p.name}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink2)' }}>{p.kind || 'custom'}</div>
              <div style={{ fontSize: 11, color: 'var(--ink3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.careers_url ? (
                  <a href={p.careers_url} target="_blank" rel="noreferrer" style={{ color: 'var(--ink3)' }}>
                    {(() => { try { return new URL(p.careers_url!).host + new URL(p.careers_url!).pathname; } catch { return p.careers_url; } })()}
                  </a>
                ) : '—'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{p.last || '—'}</div>
              <div>
                {p.paused ? <Chip variant="warn">Paused</Chip> : <Chip variant="good">Active</Chip>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <button
                  className="btn ghost"
                  style={{ padding: '4px 8px' }}
                  onClick={() => {
                    if (confirm(`Remove ${p.name} from portals.yml?`)) deleteMut.mutate(p.name);
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function AddPortalDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [careers, setCareers] = useState('');
  const [kind, setKind] = useState('custom');

  const mut = useMutation({
    mutationFn: (body: { name: string; careers_url?: string; kind?: string }) =>
      apiPost('/api/portals', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portals'] });
      setName('');
      setCareers('');
      onClose();
    },
  });

  return (
    <Drawer open={open} onClose={onClose}>
      <div style={{ padding: '22px 24px', borderBottom: '1px solid var(--line)' }}>
        <div className="eyebrow" style={{ marginBottom: 4 }}>Sources · add portal</div>
        <div className="serif" style={{ fontSize: 26, letterSpacing: '-0.02em' }}>A new source.</div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        <Row label="Company name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Anthropic" style={inputStyle} />
        </Row>
        <Row label="Careers URL">
          <input value={careers} onChange={(e) => setCareers(e.target.value)} placeholder="https://boards.greenhouse.io/anthropic" style={inputStyle} />
        </Row>
        <Row label="Kind">
          <select value={kind} onChange={(e) => setKind(e.target.value)} style={inputStyle}>
            <option value="custom">custom (Playwright scrape)</option>
            <option value="greenhouse">greenhouse</option>
            <option value="lever">lever</option>
            <option value="ashby">ashby</option>
            <option value="workable">workable</option>
          </select>
        </Row>
        {mut.isError && (
          <div style={{ marginTop: 14, fontSize: 12, color: 'var(--bad)' }}>
            {(mut.error as Error).message}
          </div>
        )}
      </div>
      <div style={{ borderTop: '1px solid var(--line)', padding: '16px 24px', display: 'flex', gap: 10 }}>
        <button className="btn ghost" onClick={onClose}>Cancel</button>
        <div style={{ flex: 1 }} />
        <button
          className="btn accent lg"
          disabled={!name || mut.isPending}
          onClick={() => mut.mutate({ name, careers_url: careers || undefined, kind })}
        >
          {mut.isPending ? 'Saving…' : 'Add portal'}
        </button>
      </div>
    </Drawer>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ────────────────────────────────────────────────────────────────────────────

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '10px 0', borderTop: '1px solid var(--line2)' }}>
      <span className="tab" style={{ marginBottom: 6 }}>{label}</span>
      {children}
    </div>
  );
}

function Toggle({ label, on, onToggle }: { label: string; on: boolean; onToggle: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderTop: '1px solid var(--line2)' }}>
      <div style={{ fontSize: 13 }}>{label}</div>
      <div
        onClick={onToggle}
        style={{ width: 38, height: 22, borderRadius: 12, background: on ? 'var(--pri)' : 'var(--line)', position: 'relative', cursor: 'pointer', transition: 'all .15s' }}
      >
        <div
          style={{
            position: 'absolute', top: 2, left: on ? 18 : 2,
            width: 18, height: 18, borderRadius: '50%',
            background: '#fff', transition: 'all .15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        />
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 6,
  fontSize: 13, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)',
  width: '100%',
};
